import React, { useState, useEffect, useRef } from "react";
import {
  Button, Dialog, DialogTitle,
  DialogContent, DialogActions, Box, Typography, IconButton,
  Snackbar, Alert, Chip, Tooltip, CircularProgress
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { ValidatorForm } from "react-material-ui-form-validator";
import { Grid } from "@material-ui/core";
import axios from "../../axios-order";
import { useHistory } from "react-router-dom";
import classes from "./Reporting.module.css";
import ValidatedDatePicker from "../../components/ui/monthWiseDatePicker";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import "../../HOC/layout/layout.css";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from '@mui/icons-material/Business';
import { Close as CloseIcon } from '@mui/icons-material';
import Inputfield from "../../components/ui/inputField";
import Modal from "@mui/material/Modal";
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TableChartIcon from '@mui/icons-material/TableChart';
import { canWrite } from '../../utils/privilegeUtils'

// ---------------------------------------------------------------------------
// Design tokens — same indigo/violet system used in Gstr1aFilingLogReport.jsx
// so both screens read as one consistent product.
// ---------------------------------------------------------------------------
const COLORS = {
  headerBg: '#03406d',        // your topbar color
  headerBgSoft: '#0b5a91',    // lighter, more saturated blue — gradient end
  headerText: '#eaf3fa',
  rowAlt: '#eef5fb',           // pale blue tint
  rowHover: '#dbeaf5',
  rowSelected: '#c3ddf0',
  border: '#d7e4ee',
  primary: '#0f6fb0',          // brighter mid-blue for icons/links/buttons
  primarySoft: '#dbeaf5',
};

const STATUS_STYLE = {
  Pass: { bg: '#dcfce7', color: '#15803d' },
  Passed: { bg: '#dcfce7', color: '#15803d' },
  Success: { bg: '#dcfce7', color: '#15803d' },
  Fail: { bg: '#fee2e2', color: '#b91c1c' },
  Failed: { bg: '#fee2e2', color: '#b91c1c' },
  'In-Progress': { bg: '#fef3c7', color: '#92400e' },
  'In-progress': { bg: '#fef3c7', color: '#92400e' },
  Pending: { bg: '#fef3c7', color: '#92400e' },
  Uploads: { bg: '#e0e7ff', color: '#3730a3' },
};
const statusChipSx = (status) => {
  const s = STATUS_STYLE[status] || { bg: '#f1f5f9', color: '#475569' };
  return { backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '11.5px', height: 22 };
};

const thSx = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  background: `linear-gradient(180deg, ${COLORS.headerBg} 0%, ${COLORS.headerBgSoft} 100%)`,
  color: COLORS.headerText,
  fontWeight: 600,
  fontSize: '14px',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  padding: '8px 10px',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  borderBottom: `2px solid ${COLORS.headerBg}`,
};

const tdSx = { padding: '7px 10px', fontSize: '14px', color: '#334155', borderBottom: `1px solid ${COLORS.border}`, textAlign: 'center' };

const rowSx = (idx) => ({ backgroundColor: idx % 2 === 0 ? '#ffffff' : COLORS.rowAlt, transition: 'background-color 0.12s ease' });

const actionIconSx = (enabled, color = COLORS.primary) => ({
  color: enabled ? color : '#c7ccd6',
  cursor: enabled ? 'pointer' : 'not-allowed',
  fontSize: 25,
  borderRadius: '50%',
  padding: '3px',
  transition: 'background-color 0.15s ease, transform 0.15s ease',
  '&:hover': enabled ? { backgroundColor: COLORS.rowSelected || COLORS.primarySoft, transform: 'scale(1.15)' } : {},
});

// Adds a hover highlight to a table row without permanently overriding its
// zebra/selected background (restored on mouse leave).
const rowHoverProps = (idx) => ({
  onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = COLORS.rowHover; },
  onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = rowSx(idx).backgroundColor; },
});

// ---------------------------------------------------------------------------
// SectionCard — replaces BoxWrapper for sections in this file. Its header
// uses the same indigo gradient as the table headers below it, so the title
// bar and the table read as one continuous piece instead of two clashing
// color systems stacked on top of each other.
// ---------------------------------------------------------------------------
const SectionCard = ({ title, icon: Icon, search, onSearchChange, searchPlaceholder, actionLabel, onAction, actionLoading, children }) => (
  <Box sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 2, overflow: 'hidden', mb: 2, boxShadow: '0 1px 3px rgba(15,23,42,0.06)' }}>
    <Box
      sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        px: 2, py: 1.1,
        background: `linear-gradient(135deg, ${COLORS.headerBg} 0%, ${COLORS.headerBgSoft} 100%)`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {Icon && <Icon sx={{ color: '#fff', fontSize: 19 }} />}
        <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.01em', fontSize: '13.5px' }}>
          {title}
        </Typography>
      </Box>
      {(onSearchChange || onAction) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {onSearchChange && (
            <input
              type="text"
              placeholder={searchPlaceholder || 'Search...'}
              onChange={onSearchChange}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', fontSize: '12.5px', width: 190, outline: 'none', backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff' }}
            />
          )}
          {onAction && (
            <Button
              size="small"
              onClick={onAction}
              variant="outlined"
              disabled={!!actionLoading}
              startIcon={actionLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : null}
              sx={{
                color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontSize: '12px', py: 0.25, textTransform: 'none',
                '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.3)' },
              }}
            >
              {actionLabel}
            </Button>
          )}
        </Box>
      )}
    </Box>
    <Box sx={{ p: 1.5, backgroundColor: '#fff' }}>{children}</Box>
  </Box>
);

export default function Reporting() {
  const [month, setMonth] = useState(null);
  const [year, setYear] = useState(null);
  const [forDisplayingFromMonthAndYear, setForDisplayingFromMonthAndYear] = useState(null);
  const [inputValues, setInputValues] = useState("optionA");
  const [tableData, setTableData] = useState([]);
  const [logData, setLogData] = useState([]);
  const [gstr1FillingSummaryData, setGstr1FillingSummaryData] = useState({});
  const [tableDataSearch, setTableDataSearch] = useState([]);
  const [viewSummary, setViewSummaryDialogOpen] = useState(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [openEstablisConnectionDrawer, setOpenEstablisConnectionDrawer] =
    useState(false);
  const form = useRef("form");
  const history = useHistory();
  const [openOTPModal, setOpenOTPModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [username, setUserName] = useState("");
  const [otpApiData, setOtpApidata] = useState({});
  const [establishConnectionButton, setEstablisConnectionButton] = useState(
    "button-outlined-secondary"
  );
  const [authAPiResData, setAuthApiResData] = useState({});
  const [vyanaList, setVyanaList] = useState([]);
  const [fileId, setFileId] = useState('');
  const [logId, setLogId] = useState('');
  const [period, setPeriod] = useState('');
  const [auth_otp, setAuthOTP] = useState("");
  const [filingStatus, setFilingStatus] = useState(false);
  const [isDataLoadingEnabled, setIsDataLoadingEnabled] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isPreviousMonth, setIsPreviousMonth] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedReferenceNo, setSelectedReferenceNo] = useState(null);
  const [selectedLogId, setSelectedLogId] = useState(false);
  const [reportData, setReportDate] = useState(null)

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Generic per-action loading map: lets any click-triggered API call show a
  // spinner on just the control that was clicked, without needing a
  // dedicated boolean state for every single button/icon.
  const [loadingMap, setLoadingMap] = useState({});
  const setLoadingKey = (key, value) => {
    setLoadingMap((prev) => ({ ...prev, [key]: value }));
  };

  const handleLog = () => {
    const log_id = localStorage.getItem('selected_log_id');

    if (!log_id) {
      console.error('No log_id found in localStorage');
      return;
    }
    setSelectedLogId(true);
    setLoadingKey('handleLog', true);
    axios
      .get(`/gstr1a_filing_prev_records/${log_id}/${localStorage.getItem('business_entity_id')}`)
      .then((res) => {
        const records = res.data
          ? Array.isArray(res.data)
            ? res.data
            : [res.data]
          : [];
        setReportDate(records);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setReportDate([]);
      })
      .finally(() => {
        setLoadingKey('handleLog', false);
      });

  }

  const handleCheckboxChange = (id, isChecked) => {
    const updatedReportData = reportData.map((item) =>
      item.id === id
        ? { ...item, selected: isChecked ? true : null }
        : item
    );
    setReportDate(updatedReportData);
  };

  const submitRecord = () => {
    const selectedData = reportData.filter((item) => item.selected !== null);
    const data = {
      record: selectedData.map((item) => ({
        id: item.id,
        erp_code: item.erp_code,
        invoice_no: item.invoice_no,
        invoice_date: item.invoice_date,
        voucher_id: item.voucher_id,
        total_amount: item.total_amount,
        taxable_value: item.taxable_value,
        selected: item.selected,
      })),
    };

    setLoadingKey('submitRecord', true);
    axios
      .post(`/gstr1a_update_selected_prev_records`, data)
      .then((res) => {
        getPreviousMonthLogReport();
        setSnackbar({
          open: true,
          message: res.data.message,
          severity: 'success',
        });
        setOpenPopup(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setSnackbar({
          open: true,
          message: err.response?.data?.message || 'An error occurred',
          severity: 'error',
        });
        setLogData([]);
        setOpenPopup(false);
      })
      .finally(() => {
        setLoadingKey('submitRecord', false);
      });
  };

  const handleSelectAll = (checked) => {
    const updatedReportData = reportData.map((item) => ({
      ...item,
      selected: checked ? true : null,
    }));
    setReportDate(updatedReportData);
  };

  // Navigates to the standalone GSTR-1A Filing Log Report page instead of
  // opening an in-page dialog. See Gstr1aFilingLogReport.jsx.
  const handleOpenPopupGstr1a = (gstr1_recon_id) => {
    localStorage.setItem('selected_log_id', gstr1_recon_id);
    history.push(`/gstr1a-log-report/${gstr1_recon_id}`);
  };

const handleRefreshGstr1aReport = (value) => {
  const key = `refreshGstr1a-${value.log_id}`;
  setLoadingKey(key, true);
  axios
    .post(`/gstr1a_report_generation`, value)
    .then((res) => {
      getApiForReport();
      setSnackbar({
        open: true,
        message: res.data.message || 'Report generation triggered',
        severity: 'success',
      });
    })
    .catch((err) => {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'An error occurred',
        severity: 'error',
      });
    })
    .finally(() => {
      setLoadingKey(key, false);
    });
};

  const handleOpenPopup = (log_id) => {
    setSelectedReferenceNo(log_id);
    localStorage.setItem('selected_log_id', log_id);
    setOpenPopup(true);

    const key = `openPopup-${log_id}`;
    setLoadingKey(key, true);
    axios
      .get(`/gstr1a_recon_log/${log_id}/true`)
      .then((res) => {
        const records = res.data.records
          ? Array.isArray(res.data.records)
            ? res.data.records
            : [res.data.records]
          : [];
        setLogData(records);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLogData([]);
      })
      .finally(() => {
        setLoadingKey(key, false);
      });
  };

  const handleRun = (value) => {
    const gst = localStorage.getItem('gst');

    const payload = {
      ...value,
      gstin: gst
    };

    const key = `run-${value.log_id}`;
    setLoadingKey(key, true);
    axios
      .post(`/gstr1a_filing_report_generation_second_op`, payload)
      .then((res) => {
        getApiForReport()
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
      })
      .finally(() => {
        setLoadingKey(key, false);
      });
  }

  const getPreviousMonthLogReport = () => {
    const log_id = localStorage.getItem('selected_log_id');

    if (!log_id) {
      console.error('No log_id found in localStorage');
      return;
    }
    setLoadingKey('getPreviousMonthLogReport', true);
    axios
      .get(`/gstr1a_recon_log/${log_id}/true`)
      .then((res) => {
        const records = res.data.records
          ? Array.isArray(res.data.records)
            ? res.data.records
            : [res.data.records]
          : [];
        setLogData(records);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLogData([]);
      })
      .finally(() => {
        setLoadingKey('getPreviousMonthLogReport', false);
      });

  }

  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedReferenceNo(null);
    setSelectedLogId(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const inputHandlerForYearAndFromMonth = (v) => {
    setForDisplayingFromMonthAndYear(v);
    const current_date = new Date(v);
    const yy = current_date.getFullYear().toString();
    const mm = (current_date.getMonth() + 1).toString();
    setMonth(mm);
    setYear(yy);
  };

  const handleConfirmDialogOpen = () => {
    if (establishConnectionButton === "button-outlined-secondary" && !authToken) {
      setSnackbar({
        open: true,
        message: "Please establish connection first.",
        severity: 'warning'
      });
      setOpenEstablisConnectionDrawer(true);
      return;
    }
    setOpenConfirmDialog(true);
  };

  const handleBackNavigation = () => {
    history.push('/Filing');
  };

  const handleConfirmDialogClose = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirmSubmit = async () => {
    setOpenConfirmDialog(false);
    await submitPushEstablish();
  };

  const getCurrentLogData = () => {
    const log_id = localStorage.getItem('selected_log_id');
    return tableData.find(item => item.log_id === log_id);
  };

  const isSubmitDisabled = () => {
    const currentLogData = getCurrentLogData();
    return currentLogData?.recon_run_with_prev === true;
  };

  const handleSubmitRecord = () => {
    if (isSubmitDisabled()) {
      setSnackbar({
        open: true,
        message: "Submit is disabled for records with previous month reconciliation completed.",
        severity: 'warning'
      });
      return;
    }
    submitRecord();
  };

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: 4,
    zIndex: 1300,
  };

  const handleOpenAndCloseModel = () => {
    setMonth(null);
    setYear(null);
    setForDisplayingFromMonthAndYear(null);
    setInputValues("");
  };

  const closeEstablisConnectionDrawerHamdler = () => {
    setOpenEstablisConnectionDrawer(false);
  };

  const closeOptModalHandler = () => {
    setAuthOTP("");
    setOpenOTPModal(false);
  };

  const vyanaAuthHandler = () => {
    let payload = {
      user_name: username,
      business_entity_id: localStorage.getItem("business_entity_id"),
      app_key: otpApiData.app_key,
      transaction_id: otpApiData.transaction_id,
      otp: auth_otp,
    };

    setLoadingKey('vyanaAuth', true);
    axios
      .post("/vyana/authenticate", payload)
      .then((res) => {
        if (res.data.status === true) {
          setOpenOTPModal(false);
          setOpenEstablisConnectionDrawer(false);
          setEstablisConnectionButton("button-connection-established");
          setAuthApiResData({
            ...authAPiResData,
            sek: res.data.sek,
            auth_token: res.data.auth_token,
          });
          getApiVyanaList();
          setSnackbar({
            open: true,
            message: res.data.message,
            severity: 'success'
          });
          setUserName("");
          setTimeout(() => {
            setEstablisConnectionButton("button-outlined-secondary");
          }, 300000);
        } else {
          setSnackbar({
            open: true,
            message: res.data.error,
            severity: 'error'
          });
          setOpenOTPModal(true);
          setEstablisConnectionButton("button-outlined-secondary");
          setUserName("");
        }
      })
      .catch((error) => {
        setOpenOTPModal(true);
        setEstablisConnectionButton("button-outlined-secondary");
        setUserName("");
        setSnackbar({
          open: true,
          message: error.response?.message || error.message,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoadingKey('vyanaAuth', false);
      });
  };

  const getOtpForAuthHandler = () => {
    let payload = {
      user_name: username,
      business_entity_id: localStorage.getItem("business_entity_id"),
    };

    setLoadingKey('getOtpForAuth', true);
    axios
      .post("/vyana/send_otp", payload)
      .then((res) => {
        if (res.data.status === true) {
          if (res.data.is_authenticated === true && res.data.is_otp_sent === false) {
            setOpenOTPModal(false);
            if (res.data.message === "Connection established.") {
              setSuccessMessage(res.data.message);
              setAuthToken(res.data.auth_token);
            }
            setSnackbar({
              open: true,
              message: res.data.message,
              severity: 'success'
            });
            closeEstablisConnectionDrawerHamdler();
          } else if (res.data.is_authenticated === false && res.data.is_otp_sent === true) {
            closeEstablisConnectionDrawerHamdler();
            setOpenOTPModal(true);
            setSnackbar({
              open: true,
              message: res.data.message,
              severity: 'success'
            });
          } else {
            setOpenOTPModal(true);
            setOtpApidata({
              ...otpApiData,
              transaction_id: res.data.transaction_id,
              app_key: res.data.app_key,
            });
          }
        } else {
          setSnackbar({
            open: true,
            message: res.data.error,
            severity: 'error'
          });
          setOpenOTPModal(false);
          setEstablisConnectionButton("button-outlined-secondary");
        }
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.message || error.message,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoadingKey('getOtpForAuth', false);
      });
  };

  const onSubmitHandler = () => {
    setLoadingKey('onSubmitHandler', true);
    axios
      .post(`/gstr1a/initiate_gstr1_recon`, {
        business_entity_id: parseInt(localStorage.getItem("business_entity_id")),
        report_month: parseInt(month),
        fiscal_year: parseInt(year),
        org_gstin: localStorage.getItem('gst')
      })
      .then((res) => {
        getApiForReport();
        setForDisplayingFromMonthAndYear(null);
        setIsPreviousMonth(null);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setLoadingKey('onSubmitHandler', false);
      });
  };

  const getApiForReport = () => {
    setLoadingKey('getApiForReport', true);
    axios
      .get(`/get_gstr1a_log/${localStorage.getItem("business_entity_id")}`)
      .then((res) => {
        setTableData(res.data.records || []);
        setTableDataSearch(res.data.records || []);
      })
      .catch((err) => {
        setTableData([]);
        setTableDataSearch([]);
      })
      .finally(() => {
        setLoadingKey('getApiForReport', false);
      });
  };

  useEffect(() => {
    getApiForReport();
  }, []);

  const downloadBase64File = (contentType, base64Data, fileName) => {
    const linkSource = `data:${contentType};base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

   const handleDownloadExcel = (file_id) => {
     const key = `downloadExcel-${file_id}`;
     setLoadingKey(key, true);
     axios
       .post(`/download_gstr_upload_file`, {
         file_id: file_id,
         extension: "xlsx",
       })
       .then((res) => {
         if (res.data.status === "success" && res.data.download_url) {
           window.location.href = res.data.download_url;
         } else {
           setSnackbar({
             open: true,
             message: "Unable to download!",
             severity: 'error'
           });
         }
       })
       .catch((error) => {
         setSnackbar({
           open: true,
           message: "Server Error, Try After Some Time",
           severity: 'error'
         });
       })
       .finally(() => {
         setLoadingKey(key, false);
       });
   };

  const handleAlternateDownload = (log_id) => {
    const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    const filename = `GSTR-1_ErrorFiling.xlsx`;

    const key = `altDownload-${log_id}`;
    setLoadingKey(key, true);
    axios
      .post(`/gst1a_save_error_report`, { log_id })
      .then((res) => {
        if (res.data.status === "pass") {
          downloadBase64File(contentType, res.data.file, filename);
        } else {
          setSnackbar({
            open: true,
            message: res.data.description || "Unable to download",
            severity: 'error'
          });
        }
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.description || "Server error, please try again later",
          severity: 'error'
        });
      })
      .finally(() => {
        setLoadingKey(key, false);
      });
  };

  const handleSearchEvent = (e) => {
    let searchValue = e.target.value.trim();
    const searchFile = tableDataSearch.filter((v) => {
      return (
        String(v.period).toLowerCase().indexOf(searchValue.toLowerCase()) >= 0 ||
        String(v.date).indexOf(searchValue) >= 0 ||
        String(v.status).toLowerCase().indexOf(searchValue.toLowerCase()) >= 0
      );
    });
    setTableData(searchFile);
  };

  const handleViewSummaryDialog = (item) => {
    const log_id = item.log_id
    setViewSummaryDialogOpen(true);
    setIsLoadingSummary(true);
    axios
      .get(`/gstr1a_view_summary_gst_save/${log_id}`)
      .then((res) => {
        setGstr1FillingSummaryData(res.data || {});
        setIsLoadingSummary(false);
        setFileId(item.file_id || '');
        setLogId(item.log_id || '');
        setPeriod(item.period || '');
        setFilingStatus(res.data.filing_status === true);
      })
      .catch((err) => {
        setGstr1FillingSummaryData({});
        setIsLoadingSummary(false);
        setFileId('');
        setLogId('');
        setPeriod('');
        setSnackbar({
          open: true,
          message: "Failed to fetch summary data",
          severity: 'error'
        });
      });
  };

  const handleViewSummaryCloseDialog = () => {
    setViewSummaryDialogOpen(false);
    setGstr1FillingSummaryData({});
  };

  const getApiVyanaList = () => {
    axios
      .get(`/vyana/get_list/${localStorage.getItem("business_entity_id")}`)
      .then((res) => {
        if (res.data.status === true) {
          setVyanaList(res.data.data);
        }
      })
      .catch(() => {});
  };

  const submitPushEstablish = () => {
    const payload = {
      business_entity_id: localStorage.getItem("business_entity_id"),
      user_name: username,
      transaction_id: otpApiData.transaction_id,
      app_key: otpApiData.app_key,
      sek: authAPiResData.sek,
      auth_token: authAPiResData.auth_token,
      file_id: fileId,
      log_id: logId,
      period: period,
    };

    setIsDataLoadingEnabled(true);
    axios
      .post("/vyana/gst_save", payload)
      .then((res) => {
        setIsDataLoadingEnabled(false);
        if (res.data.status === true) {
          getApiVyanaList();
          getApiForReport();
          setSnackbar({
            open: true,
            message: "GSTR-1 filed successfully.",
            severity: 'success'
          });
        } else {
          getApiVyanaList();
          setSnackbar({
            open: true,
            message: res.data.error?.error?.message || "Failed to file GSTR-1A.",
            severity: 'error',
          });
        }
        handleViewSummaryCloseDialog();
      })
      .catch((error) => {
        setIsDataLoadingEnabled(false);
        setSnackbar({
          open: true,
          message: "Failed to file GSTR-1. Please try again.",
          severity: 'error'
        });
        handleViewSummaryCloseDialog();
      });
  };

  const Gstr1FillingSummaryDialog = ({ gstr1FillingSummaryData, isLoadingSummary }) => {
    const transformOverallSummary = (overallSummary) => {
      if (!overallSummary || !overallSummary["Grand Total"]) return [];

      const transformed = [];

      if (overallSummary["Grand Total"]) {
        transformed.push({
          "Particulars (Sheet Name)": "Grand Total",
          "Invoice Count": overallSummary["Grand Total"]["Invoice Count"] ?? "-",
          "Taxable Value": overallSummary["Grand Total"]["Taxable Value"] ?? 0,
          "Tax Value": overallSummary["Grand Total"]["Tax Value"] ?? 0,
          "Invoice Value": overallSummary["Grand Total"]["Invoice Value"] ?? 0,
          bold: true,
        });
      }

      Object.keys(overallSummary).forEach((key) => {
        if (key.startsWith("HSN Summary")) {
          const item = overallSummary[key] || {};
          transformed.push({
            "Particulars (Sheet Name)": key,
            "Invoice Count": item["Invoice Count"] ?? "-",
            "Taxable Value": item["Taxable Value"] ?? 0,
            "Tax Value": item["Tax Value"] ?? 0,
            "Invoice Value": item["Invoice Value"] ?? 0,
            bold: true,
          });
        }
      });

      if (overallSummary["Difference"]) {
        const diff = overallSummary["Difference"];
        transformed.push({
          "Particulars (Sheet Name)": "Difference",
          "Invoice Count": diff["Invoice Count"] ?? "-",
          "Taxable Value": diff["Taxable Value"] ?? 0,
          "Tax Value": diff["Tax Value"] ?? 0,
          "Invoice Value": diff["Invoice Value"] ?? 0,
          bold: true,
        });
      }

      return transformed;
    };

    const combineSummaryData = (data) => {
      const summaryReport = (data.summary_report || []).map((item, index) => ({
        ...item,
        sNo: index + 1,
        bold: false,
      }));
      const overallSummary = transformOverallSummary(data.overall_summary || {});
      return [...summaryReport, ...overallSummary];
    };

    const formatRupee = (amount) =>
      new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount ?? 0);

    const summaryTableData = gstr1FillingSummaryData
      ? combineSummaryData(gstr1FillingSummaryData)
      : [];

    return (
      <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thSx}>S.No</th>
              <th style={thSx}>Particulars</th>
              <th style={thSx}>Invoice Count</th>
              <th style={thSx}>Taxable Value/Non-Taxable Value</th>
              <th style={thSx}>Tax Value</th>
              <th style={thSx}>Invoice Value</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingSummary ? (
              <tr><td style={{ ...tdSx, color: COLORS.primary }} colSpan={6}>Loading...</td></tr>
            ) : summaryTableData.length > 0 ? (
              summaryTableData.map((value, index) => {
                const sheetName = value["Particulars (Sheet Name)"];
                const isSpecialRow =
                  sheetName === "Grand Total" ||
                  sheetName === "Difference" ||
                  sheetName.startsWith("HSN Summary");
                const cellSx = { ...tdSx, fontWeight: value.bold ? 700 : 400, backgroundColor: isSpecialRow ? COLORS.rowAlt : (index % 2 === 0 ? '#fff' : COLORS.rowAlt) };

                return (
                  <tr key={index}>
                    {isSpecialRow ? (
                      <>
                        <td style={cellSx} colSpan={2}>{value["Particulars (Sheet Name)"] || "-"}</td>
                        <td style={cellSx}>{value["Invoice Count"] ?? "-"}</td>
                        <td style={cellSx}>{formatRupee(value["Taxable Value"])}</td>
                        <td style={cellSx}>{formatRupee(value["Tax Value"])}</td>
                        <td style={cellSx}>{formatRupee(value["Invoice Value"])}</td>
                      </>
                    ) : (
                      <>
                        <td style={cellSx}>{value.sNo || "-"}</td>
                        <td style={cellSx}>{value["Particulars (Sheet Name)"] || "-"}</td>
                        <td style={cellSx}>{value["Invoice Count"] ?? "-"}</td>
                        <td style={cellSx}>{formatRupee(value["Taxable Value"])}</td>
                        <td style={cellSx}>{formatRupee(value["Tax Value"])}</td>
                        <td style={cellSx}>{formatRupee(value["Invoice Value"])}</td>
                      </>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr><td style={{ ...tdSx, color: '#b91c1c' }} colSpan={6}>No Data Found!!</td></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const downloadBase64Excel = (contentType, base64Data, fileName) => {
    const linkSource = `data:${contentType};base64,${base64Data}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = fileName;
    downloadLink.click();
  };

  const handleDownloadSaveExcel = (reference_no) => {
    const business_entity_id = localStorage.getItem("business_entity_id");
    const reference_id = reference_no;
    const payload = { business_entity_id, reference_id };
    const key = `downloadSave-${reference_no}`;
    setLoadingKey(key, true);
    axios
      .post("/vyana/gst_return_status_download_excel", payload)
      .then((response) => {
        if (response.data.status === "success") {
          setSnackbar({ open: true, message: response.data.message, severity: 'success' });
        } else if (response.data.status === "error") {
          setSnackbar({ open: true, message: response.data.message, severity: 'error' });
        }
        const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const base64Data = response.data.file;
        if (base64Data) {
          downloadBase64Excel(contentType, base64Data, "GSTR1_Save_Status.xlsx");
        }
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoadingKey(key, false);
      });
  };

  const handleClick = async (reference_no) => {
    const key = `checkStatus-${reference_no}`;
    setLoadingKey(key, true);
    try {
      const business_entity_id = localStorage.getItem("business_entity_id");
      const reference_id = reference_no;
      const payload = { business_entity_id, reference_id };
      const response = await axios.post("/vyana/gst_return_status/", payload);

      if (response.data.status === "success") {
        setSnackbar({ open: true, message: response.data.message, severity: 'success' });
      } else if (response.data.status === "error") {
        setSnackbar({ open: true, message: response.data.message, severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Error checking GST return status", severity: 'error' });
    } finally {
      setLoadingKey(key, false);
    }
  };

  const handleRefresh = (item) => {
    const payloadData = {
      "business_entity_id": localStorage.getItem("business_entity_id"),
      "reference_id": item.reference_no,
      "period": item.period
    }
    const key = `refreshSave-${item.reference_no}`;
    setLoadingKey(key, true);
    axios.post("/vyana/get_gstr_save_updated_status", payloadData)
      .then(() => {
        getApiVyanaList();
        getApiForReport();
      }).catch(() => {})
      .finally(() => {
        setLoadingKey(key, false);
      })
  }

  return (
    <React.Fragment>
      {/* ---- Compact top bar ---- */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
          backgroundColor: '#f5f5f5',
          borderBottom: `1px solid ${COLORS.border}`,
        }}
      >
        <Tooltip title="Back" arrow>
          <IconButton
            onClick={handleBackNavigation}
            sx={{
              borderRadius: '50%',
              padding: '5px',
              color: '#263238',
              backgroundColor: '#ffffff',
              height: '30px',
              width: '30px',
              '&:hover': { backgroundColor: '#455a64', color: '#ffffff' },
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
            }}
          >
            <ArrowBackIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
        {
          successMessage === "Connection established." ? (
            canWrite("GSTR1-FILING") && (
              <button
                className="button-outlined-primary"
                style={{ fontSize: '13px', padding: '4px 8px', backgroundColor: '#03406d', color: 'white' }}
              >
                Connection Established
              </button>
            )
          ) : (
            canWrite("GSTR1-FILING") && (
              <button
                className={establishConnectionButton}
                onClick={() => setOpenEstablisConnectionDrawer(true)}
              >
                {establishConnectionButton === "button-outlined-secondary"
                  ? "Establish Connection"
                  : "Connection Established"
                }
              </button>
            )
          )}
      </Box>

      {canWrite("GSTR1-FILING") && (
        <SectionCard title="GSTR-1A Filing" icon={CalendarMonthIcon}>
          <ValidatorForm
            useRef="form"
            onSubmit={onSubmitHandler}
            onError={(errors) => console.log(errors)}
          >
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: '#334155' }}>
                  Reporting Month <span style={{ color: '#dc2626' }}>*</span>
                </Typography>
                <ValidatedDatePicker
                  placeholder="Month & Year"
                  errorMessages={["This field is required"]}
                  validators={["required"]}
                  value={forDisplayingFromMonthAndYear}
                  onChange={(v) => inputHandlerForYearAndFromMonth(v)}
                  fullWidth
                />
              </Grid>
              <Grid item>
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!!loadingMap['onSubmitHandler']}
                    startIcon={loadingMap['onSubmitHandler'] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
                    sx={{
                      backgroundColor: COLORS.primary, textTransform: 'none', fontWeight: 600, px: 3, boxShadow: 'none',
                      '&:hover': { backgroundColor: COLORS.headerBgSoft, boxShadow: 'none' },
                    }}
                  >
                    Submit
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleOpenAndCloseModel}
                    sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderColor: COLORS.border, color: '#475569', '&:hover': { borderColor: COLORS.primary, color: COLORS.primary, backgroundColor: COLORS.rowAlt } }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </ValidatorForm>
        </SectionCard>
      )}

      <SectionCard
        title="GSTR-1A Filing Report"
        icon={TableChartIcon}
        onSearchChange={handleSearchEvent}
        searchPlaceholder="Search..."
        actionLabel="Refresh"
        onAction={getApiForReport}
        actionLoading={loadingMap['getApiForReport']}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thSx}>Period</th>
                <th style={thSx}>Report Generated Date</th>
                <th style={thSx}>GSTR 1A</th>
                <th style={thSx}>Previous Month</th>
                <th style={thSx}>Filing Status</th>
                <th style={thSx}>GSTR Save Status</th>
                <th style={thSx}>GSTR Save Error</th>
                <th style={thSx}>Report</th>
                <th style={thSx}>Error Report</th>
                <th style={thSx}>View Summary</th>
              </tr>
            </thead>
            <tbody>
              {tableData?.map((value, idx) => (
               <tr key={value.log_id} style={rowSx(idx)} {...rowHoverProps(idx)}>
                  <td style={tdSx}>{value.period}</td>
                  <td style={tdSx}>{value.date}</td>
                 <td style={tdSx}>
  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
    {loadingMap[`openPopup-${value.log_id}`] ? (
      <CircularProgress size={18} sx={{ color: COLORS.primary }} />
    ) : (
      <VisibilityIcon
        sx={actionIconSx(true)}
        onClick={() => handleOpenPopupGstr1a(value.gstr1_recon_id)}
      />
    )}
    {value.status === 'Partial-Process' && (
      loadingMap[`refreshGstr1a-${value.log_id}`] ? (
        <CircularProgress size={18} sx={{ color: COLORS.primary }} />
      ) : (
        <RefreshIcon
          sx={actionIconSx(true)}
          onClick={() => handleRefreshGstr1aReport(value)}
        />
      )
    )}
  </Box>
</td>
                  <td style={tdSx}>
                    {value.is_previous_month === true && value.recon_run_with_prev !== true && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {loadingMap[`openPopup-${value.log_id}`] ? (
                          <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                        ) : (
                          <VisibilityIcon sx={actionIconSx(true)} onClick={() => handleOpenPopup(value.log_id)} />
                        )}
                        {loadingMap[`run-${value.log_id}`] ? (
                          <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                        ) : (
                          <RefreshIcon sx={actionIconSx(true)} onClick={() => handleRun(value)} />
                        )}
                      </Box>
                    )}
                    {value.recon_run_with_prev === true && (
                      loadingMap[`openPopup-${value.log_id}`] ? (
                        <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                      ) : (
                        <VisibilityIcon sx={actionIconSx(true)} onClick={() => handleOpenPopup(value.log_id)} />
                      )
                    )}
                  </td>
                  <td style={tdSx}><Chip label={value.status} size="small" sx={statusChipSx(value.status)} /></td>
                  <td style={tdSx}><Chip label={value.gstr1_save_status} size="small" sx={statusChipSx(value.gstr1_save_status)} /></td>
                  <td style={tdSx}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                      {loadingMap[`downloadSave-${value.reference_no}`] ? (
                        <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                      ) : (
                        <DownloadIcon
                          sx={actionIconSx(!!(value.reference_no && (value.gstr1_save_status === 'Fail' || value.reference_no)))}
                          onClick={() => {
                            if (value.reference_no && (value.gstr1_save_status === 'Fail' || value.reference_no)) {
                              handleDownloadSaveExcel(value.reference_no);
                            }
                          }}
                        />
                      )}
                      {loadingMap[`checkStatus-${value.reference_no}`] ? (
                        <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                      ) : (
                        <VisibilityIcon
                          sx={actionIconSx(true, value.gstr1_save_status === 'Fail' ? '#b91c1c' : COLORS.primary)}
                          onClick={() => handleClick(value.reference_no)}
                        />
                      )}
                      {value.gstr1_save_status === 'In-progress' && (
                        loadingMap[`refreshSave-${value.reference_no}`] ? (
                          <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                        ) : (
                          <RefreshIcon sx={actionIconSx(true)} onClick={() => handleRefresh(value)} />
                        )
                      )}
                    </Box>
                  </td>
                  <td style={tdSx}>
                    {loadingMap[`downloadExcel-${value.file_id}`] ? (
                      <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                    ) : (
                      <DownloadIcon
                        sx={actionIconSx(!!(value.file_id != null && !(value.status === 'In-Progress' || value.status === 'Uploads')))}
                        onClick={() => {
                          if (value.file_id != null && !(value.status === 'In-Progress' || value.status === 'Uploads')) {
                            handleDownloadExcel(value.file_id);
                          }
                        }}
                      />
                    )}
                  </td>
                  <td style={tdSx}>
                    {loadingMap[`altDownload-${value.log_id}`] ? (
                      <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                    ) : (
                      <DownloadIcon
                        sx={actionIconSx(!(value.status === 'In-Progress' || value.status === 'Uploads') && value.status === 'Fail')}
                        onClick={() => {
                          if (!(value.status === 'In-Progress' || value.status === 'Uploads') && value.status === 'Fail') {
                            handleAlternateDownload(value.log_id);
                          }
                        }}
                      />
                    )}
                  </td>
                  <td style={tdSx}>
                    <VisibilityIcon
                      sx={actionIconSx(!(value.status === 'In-Progress' || value.status === 'Uploads') && value.status === 'Pass')}
                      onClick={() => {
                        if (!(value.status === 'In-Progress' || value.status === 'Uploads') && value.status === 'Pass') {
                          handleViewSummaryDialog(value);
                        }
                      }}
                    />
                  </td>
                </tr>
              ))}
              {tableData.length <= 0 && (
                <tr><td style={{ ...tdSx, color: '#b91c1c' }} colSpan={10}>No Data Found!!</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* ---- Previous month popup ---- */}
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 3, boxShadow: 24, px: 2, py: 1, bgcolor: 'background.default' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">GSTR-1A Filing Log Report Previous Month</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <SectionCard
            title="GSTR-1A Filing Log Report Previous Month"
            icon={TableChartIcon}
            actionLabel="Refresh"
            onAction={getPreviousMonthLogReport}
            actionLoading={loadingMap['getPreviousMonthLogReport']}
          >
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thSx}>Run Date</th>
                    <th style={thSx}>Report Month</th>
                    <th style={thSx}>Status</th>
                    <th style={thSx}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(logData) && logData.length > 0 ? (
                    logData.map((value, idx) => (
                      <tr key={value.log_id} style={rowSx(idx)} {...rowHoverProps(idx)}>
                        <td style={tdSx}>{value.record_run_date}</td>
                        <td style={tdSx}>{value.report_month}</td>
                        <td style={tdSx}><Chip label={value.status} size="small" sx={statusChipSx(value.status)} /></td>
                        <td style={tdSx}>
                          {loadingMap['handleLog'] ? (
                            <CircularProgress size={18} sx={{ color: COLORS.primary }} />
                          ) : (
                            <VisibilityIcon sx={actionIconSx(value.status === 'Pass')} onClick={() => value.status === 'Pass' && handleLog(value.log_id)} />
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td style={{ ...tdSx, color: '#b91c1c' }} colSpan={4}>No Data Found!!</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
          {selectedLogId && (
            <SectionCard
              title="Report of Previous Month"
              icon={TableChartIcon}
              actionLabel="Submit"
              onAction={handleSubmitRecord}
              actionLoading={loadingMap['submitRecord']}
            >
              <div style={{ overflowX: 'auto', marginTop: 4 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thSx, width: 40 }}>
                        <input
                          type="checkbox"
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={isSubmitDisabled()}
                          checked={Array.isArray(reportData) && reportData.length > 0 && reportData.every((item) => item.selected !== null)}
                        />
                      </th>
                      <th style={thSx}>ERP Code</th>
                      <th style={thSx}>Invoice No</th>
                      <th style={thSx}>Invoice Date</th>
                      <th style={thSx}>Invoice Type</th>
                      <th style={thSx}>Voucher Id</th>
                      <th style={thSx}>Total Amount</th>
                      <th style={thSx}>Taxable Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(reportData) && reportData.length > 0 ? (
                      reportData.map((value, idx) => (
                        <tr key={value.log_id} style={rowSx(idx)} {...rowHoverProps(idx)}>
                          <td style={tdSx}>
                            <input
                              type="checkbox"
                              disabled={isSubmitDisabled()}
                              checked={value.selected !== null}
                              onChange={(e) => handleCheckboxChange(value.id, e.target.checked)}
                            />
                          </td>
                          <td style={tdSx}>{value.erp_code}</td>
                          <td style={tdSx}>{value.document_no}</td>
                          <td style={tdSx}>{value.document_date}</td>
                          <td style={tdSx}>{value.document_type}</td>
                          <td style={tdSx}>{value.voucher_id}</td>
                          <td style={tdSx}>{value.total_amount}</td>
                          <td style={tdSx}>{value.taxable_value}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td style={{ ...tdSx, color: '#b91c1c' }} colSpan={8}>No Data Found!!</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Filing summary dialog ---- */}
      <Dialog
        open={viewSummary}
        onClose={handleViewSummaryCloseDialog}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: 3, boxShadow: 24, px: 2, py: 1, bgcolor: 'background.default' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">GSTR-1A Filing Summary</Typography>
          </Box>
          <IconButton
            onClick={handleViewSummaryCloseDialog}
            size="small"
            disabled={isDataLoadingEnabled}
            sx={{
              transition: 'background-color 0.15s ease',
              '&:hover': { backgroundColor: COLORS.rowSelected },
              '&.Mui-disabled': { opacity: 0.5, cursor: 'not-allowed' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          <Gstr1FillingSummaryDialog
            gstr1FillingSummaryData={gstr1FillingSummaryData}
            isLoadingSummary={isLoadingSummary}
          />
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end', px: 3, py: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
          {isDataLoadingEnabled && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<HourglassBottomIcon sx={{ animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />}
              disabled
            >
              Filing...
            </Button>
          )}
          {!isDataLoadingEnabled && (
            <>
              {canWrite("GSTR1-FILING") && (
                successMessage === "Connection established." ? (
                  <button
                    className="button-outlined-primary"
                    style={{ fontSize: '13px', padding: '4px 8px', backgroundColor: '#03406d', color: 'white' }}
                  >
                    Connection Established
                  </button>
                ) : (
                  <button className={establishConnectionButton} onClick={() => setOpenEstablisConnectionDrawer(true)}>
                    {establishConnectionButton === "button-outlined-secondary" ? "Establish Connection" : "Connection Established"}
                  </button>
                )
              )}
              {canWrite("GSTR1-FILING") && (
                <Button
                  className={classes.buttonprimary}
                  onClick={handleConfirmDialogOpen}
                  variant="outlined"
                  type="submit"
                  disabled={!filingStatus}
                  sx={{
                    '&.Mui-disabled': { color: '#7393B3 !important', cursor: 'not-allowed !important' },
                    '&.Mui-disabled:hover': { cursor: 'not-allowed !important' },
                  }}
                >
                  File GSTR - 1A
                </Button>
              )}
              <Button onClick={handleViewSummaryCloseDialog} variant="outlined" disabled={isDataLoadingEnabled}>
                Close
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={openConfirmDialog}
        onClose={handleConfirmDialogClose}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 24, p: 2, bgcolor: 'background.default' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm GSTR-1A Filing</DialogTitle>
        <DialogContent>
          <Typography>Do you really want to file GSTR-1?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmSubmit} variant="contained" color="primary">Yes</Button>
          <Button onClick={handleConfirmDialogClose} variant="outlined" color="secondary">No</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEstablisConnectionDrawer}
        onClose={closeEstablisConnectionDrawerHamdler}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2, boxShadow: 24, p: 2, bgcolor: 'background.default' } }}
      >
        <DialogTitle style={{ fontWeight: '600' }}>Establish Connection</DialogTitle>
        <DialogContent>
          <ValidatorForm ref={form} onSubmit={getOtpForAuthHandler} onError={(errors) => console.log(errors)}>
            <Inputfield
              textLabel="User Name"
              inputType="text"
              name="user_name"
              value={username}
              textOnchangeFunction={(e) => setUserName(e.target.value)}
              requiredField="*"
              validator={["required"]}
              errorMsg={["This field is required"]}
            />
            <div className="mt-20">
              <button className="button-primary" type="submit" disabled={!!loadingMap['getOtpForAuth']}>
                {loadingMap['getOtpForAuth'] ? (
                  <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />
                ) : null}
                Submit
              </button>
              <button className="button-outlined-secondary" type="button" onClick={closeEstablisConnectionDrawerHamdler}>Close</button>
            </div>
          </ValidatorForm>
        </DialogContent>
      </Dialog>

      <Modal open={openOTPModal}>
        <Box sx={modalStyle}>
          <ValidatorForm ref={form} onSubmit={vyanaAuthHandler} onError={(errors) => console.log("ValidatorForm errors:", errors)}>
            <Inputfield
              textLabel="OTP"
              inputType="text"
              name="OTP"
              value={auth_otp}
              textOnchangeFunction={(e) => setAuthOTP(e.target.value)}
              requiredField="*"
              validator={["required"]}
              errorMsg={["This field is required"]}
            />
            <div className="mt-20">
              <button className="button-primary" type="submit" disabled={!!loadingMap['vyanaAuth']}>
                {loadingMap['vyanaAuth'] ? (
                  <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} />
                ) : null}
                Submit
              </button>
              <button className="button-outlined-secondary" type="button" onClick={closeOptModalHandler}>Close</button>
            </div>
          </ValidatorForm>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
}